import React, { useContext, useState } from 'react';
import type { FieldProps, InputProps } from '@fluentui/react-components';
import {
    FluentProvider, Theme, webLightTheme,
    makeStyles, useId, shorthands, tokens,
    Dialog, DialogContent, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogTrigger,
    Dropdown, Option, DropdownProps,
    Checkbox, CheckboxOnChangeData,
    Field, Input, Label,
    Button,
    Tooltip,
} from '@fluentui/react-components';
import { DealSeats, CardSeatsStringMap } from './BridgeTypes';
import { GameSettingsContext, GameSettingsState, GameSettingsContextValue } from './BridgeEngineSettings';
import { PlayStateContext } from './BridgeEnginePlay';
import './Dialog.css';

const useStyles = makeStyles({
    input: {
        maxWidth: '4em',
    },
    dropdown: {
        maxWidth: '8em',
        minWidth: '8em',
    },
    listbox: {
        maxWidth: '8em',
        minWidth: '8em',
    },
});

interface DialogSetupProps {
    handleOnClose: () => void;
}

const DialogSetup: React.FC<DialogSetupProps> = ({ handleOnClose }) => {
    const gameSettingsContext = useContext(GameSettingsContext)!;
    const { gameSettings, updateGameSettings } = gameSettingsContext;
    
    const playStateContext = useContext(PlayStateContext)!;

    const [isDialogVisible, setIsDialogVisible] = useState(true);

    const [oneshotBoardValue, setOneshotBoardValue] = useState('');
    const [oneshotBoardMessage, setOneshotBoardMessage] = useState('');
    const [oneshotBoardState, setOneshotBoardState] = useState<'none' | 'error' | 'warning' | 'success'>('none');

    const [oneshotPlayerSeat, setOneshotPlayerSeat] = useState<DealSeats | null>(null);
    const [oneshotSeatString, setOneshotSeatString] = useState<string[]>(['']);

    const [useFixedPlayerSeat, setUseFixedPlayerSeat] = useState<boolean>(gameSettings.fixedPlayerSeat !== null);
    const [fixedPlayerSeat, setFixedPlayerSeat] = useState<DealSeats | null>(gameSettings.fixedPlayerSeat);
    const [fixedPlayerSeatString, setFixedPlayerSeatString] = useState<string[]>(fixedPlayerSeat ? [CardSeatsStringMap[fixedPlayerSeat]] : ['']);

    const handleGeneralCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) => {
        const alteredGameSettings: Partial<GameSettingsState> = {};
        switch (e.target.id) {
            case 'TenAsTCheckbox':
                alteredGameSettings.tenAsT = !!data.checked;
                break;
            case 'ShowSuitDepthCheckbox':
                alteredGameSettings.showSuitDepth = !!data.checked;
                break;
            case 'HonorsOnlyCheckbox':
                alteredGameSettings.honorsOnly = !!data.checked;
                break;
            case 'TenIsHonorCardCheckbox':
                alteredGameSettings.tenIsHonor = !!data.checked;
                break;
        };
        updateGameSettings(alteredGameSettings);
    };

    const handleFixedPlayerSeatCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>, data: CheckboxOnChangeData) => {
        setUseFixedPlayerSeat(!!data.checked);
        if (!!!data.checked) {
            setFixedPlayerSeat(null);
            setFixedPlayerSeatString(['']);
            updateGameSettings({ fixedPlayerSeat: null });
        }
    }

    const handleChangeFixedPlayerSeat: DropdownProps['onOptionSelect'] = (ev, data) => {
        const newPlayerSeat = data.selectedOptions[0] as DealSeats;
        setFixedPlayerSeat(newPlayerSeat);
        setFixedPlayerSeatString([CardSeatsStringMap[newPlayerSeat]]);
        updateGameSettings({ fixedPlayerSeat: newPlayerSeat });
    }

    const onOneshotBoardChange: InputProps['onChange'] = (ev, data) => {
        let newBoard = data.value.toUpperCase();
        switch (newBoard.length) {
            case 2:
                setOneshotBoardValue(newBoard);
                if (gameSettingsContext.doesBoardExist(newBoard)) {
                    setOneshotBoardMessage('Board found.');
                    setOneshotBoardState('success');
                    updateGameSettings({oneshotBoard: newBoard});
                } else {
                    setOneshotBoardMessage('Board not found.');
                    setOneshotBoardState('error');
                }
                break;
            case 1:
                setOneshotBoardValue(newBoard);
                setOneshotBoardMessage('Board number must be at least two digits.');
                setOneshotBoardState('warning');
                break;
            case 0:
            default:
                setOneshotBoardValue(newBoard);
                setOneshotBoardMessage('');
                setOneshotBoardState('none');
                break;
        }
    };

    const handleChangeOneshotPlayerSeat: DropdownProps['onOptionSelect'] = (e, data) => {
        const newPlayerSeat = data.selectedOptions[0] as DealSeats;
        setOneshotPlayerSeat(newPlayerSeat);
        setOneshotSeatString([CardSeatsStringMap[newPlayerSeat]]);
        updateGameSettings({oneshotPlayerSeat: newPlayerSeat});
    }

    const closeDialog = () => {
        // either of these must reset the game.
        if (fixedPlayerSeat !== null || oneshotBoardState === 'success') {
            playStateContext.newGame();
        }
        setIsDialogVisible(false);
        handleOnClose();
    }

    const styles = useStyles();
    return (
        <FluentProvider theme={webLightTheme}>
            <Dialog
                open={isDialogVisible}
                onOpenChange={closeDialog}
            >
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>Suited setup</DialogTitle>
                        <DialogContent>
                            <div className='setup-options-border'>
                                <Checkbox
                                    id='TenAsTCheckbox'
                                    label='Display "T" for 10'
                                    checked={gameSettings.tenAsT}
                                    onChange={handleGeneralCheckboxChange} />
                            </div>
                            
                            <div className='setup-oneshot-border'>
                                <label><em>Using either of these options will start a new game.</em></label>
                                <div>
                                    <Checkbox
                                        id='FixedPlayerSeat'
                                        label='Use constant player seat (you):'
                                        checked={useFixedPlayerSeat}
                                        onChange={handleFixedPlayerSeatCheckboxChange} />
                                    <Dropdown
                                        id='fixedplayer-dropdown'
                                        disabled={!useFixedPlayerSeat}
                                        selectedOptions={fixedPlayerSeatString}
                                        value={fixedPlayerSeatString[0]}
                                        onOptionSelect={handleChangeFixedPlayerSeat}
                                        className={styles.dropdown}
                                        listbox={{ className: styles.listbox }}
                                    >
                                        {Object.values(DealSeats).map((seat: DealSeats) => (
                                            <Option key={seat}
                                                value={seat}
                                            >
                                                {CardSeatsStringMap[seat]}
                                            </Option>
                                        ))}
                                    </Dropdown>
                                </div>
                                <Field
                                    label="Set board and (optionally) player's seat (you)"
                                    validationMessage={oneshotBoardMessage}
                                    validationState={oneshotBoardState}
                                >
                                    <div className='oneshot-board-and-player'>
                                        <Input
                                            id='OneshotBoardInput'
                                            value={oneshotBoardValue}
                                            onChange={onOneshotBoardChange}
                                            className={styles.input}
                                        />
                                        <Dropdown
                                            id='oneshot-dropdown'
                                            disabled={oneshotBoardState !== 'success'}
                                            clearable={true}
                                            selectedOptions={oneshotSeatString}
                                            value={oneshotSeatString[0]}
                                            onOptionSelect={handleChangeOneshotPlayerSeat}
                                            className={styles.dropdown}
                                            listbox={{ className: styles.listbox }}
                                        >
                                            {Object.values(DealSeats).map((seat: DealSeats) => (
                                                <Option key={seat}
                                                    value={seat}
                                                >
                                                    {CardSeatsStringMap[seat]}
                                                </Option>
                                            ))}
                                        </Dropdown>
                                    </div>
                                </Field>
                            </div>            
                            
                            <div className="setup-testing-border">
                                <label><em>For testing purposes only. These will be removed in final production.</em></label>
                                <div>
                                    <Checkbox
                                        id='ShowSuitDepthCheckbox'
                                        label='Show suit depth on all hands'
                                        checked={gameSettings.showSuitDepth}
                                        onChange={handleGeneralCheckboxChange} />
                                </div>
                                <div>
                                    <Checkbox
                                        id='HonorsOnlyCheckbox'
                                        label='Honors only'
                                        checked={gameSettings.honorsOnly}
                                        onChange={handleGeneralCheckboxChange} />
                                </div>
                                <div>
                                    <Checkbox
                                        id='TenIsHonorCardCheckbox'
                                        label='Ten is an honor card'
                                        checked={gameSettings.tenIsHonor}
                                        onChange={handleGeneralCheckboxChange} />
                                </div>
                            </div>
                        </DialogContent>
                        <DialogActions>
                            <DialogTrigger disableButtonEnhancement>
                                <Button appearance="primary">Close</Button>
                            </DialogTrigger>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </FluentProvider>
    );
};

export default DialogSetup;